<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ConversationsAPI from 'dashboard/api/conversations';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle-search']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings } = useUISettings();

const openContactSidebar = () => {
  updateUISettings({ is_contact_sidebar_open: true });
};

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const aiAgentEnabled = computed(() => {
  return currentChat.value?.additional_attributes?.ai_agent_enabled !== false;
});

const aiAgentToggleTooltip = computed(() => {
  return aiAgentEnabled.value
    ? t('AI_AGENTS.CONVERSATION.DISABLE_AI')
    : t('AI_AGENTS.CONVERSATION.ENABLE_AI');
});

const toggleAiAgent = async () => {
  const enabled = !aiAgentEnabled.value;
  try {
    await ConversationsAPI.update(currentChat.value.id, {
      additional_attributes: { ai_agent_enabled: enabled },
    });
    currentChat.value.additional_attributes = {
      ...(currentChat.value.additional_attributes || {}),
      ai_agent_enabled: enabled,
    };
  } catch (error) {
    // Error handled by API interceptor
  }
};

const aiAgents = computed(() => store.getters['aiAgents/getAgents'] || []);
const hasAiAgents = computed(() => aiAgents.value.length > 0);
const selectedAgentId = computed(
  () => currentChat.value?.additional_attributes?.ai_agent_id || ''
);

onMounted(() => {
  store.dispatch('aiAgents/get');
});

const showAgentList = ref(false);

const toggleAgentList = () => {
  showAgentList.value = !showAgentList.value;
};

const onAgentChange = async event => {
  const rawValue = event.target.value;
  const agentId = rawValue ? Number(rawValue) : null;
  try {
    await ConversationsAPI.update(currentChat.value.id, {
      additional_attributes: { ai_agent_id: agentId },
    });
    currentChat.value.additional_attributes = {
      ...(currentChat.value.additional_attributes || {}),
      ai_agent_id: agentId,
    };
  } catch (error) {
    // Error handled by API interceptor
  }
  showAgentList.value = false;
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <button
        type="button"
        class="p-0 border-0 shrink-0 rounded-full"
        @click="openContactSidebar"
      >
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
        />
      </button>
      <div class="flex flex-col items-start min-w-0 ms-2 overflow-hidden">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <button
            type="button"
            class="p-0 border-0 text-sm font-medium truncate leading-tight text-n-slate-12 hover:text-n-brand transition-colors"
            @click="openContactSidebar"
          >
            {{ currentContact.name }}
          </button>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <button
        v-tooltip="aiAgentToggleTooltip"
        class="p-2 rounded-lg transition-colors hover:bg-n-slate-6"
        :class="aiAgentEnabled ? 'text-n-brand' : 'text-n-slate-10'"
        @click="toggleAiAgent"
      >
        <span
          :class="aiAgentEnabled ? 'i-lucide-zap' : 'i-lucide-zap-off'"
          class="size-5"
        />
      </button>
      <button
        v-if="hasAiAgents"
        v-tooltip="t('AI_AGENTS.HEADER')"
        class="p-2 rounded-lg transition-colors hover:bg-n-slate-6"
        :class="showAgentList ? 'text-n-brand' : 'text-n-slate-10'"
        @click="toggleAgentList"
      >
        <span class="i-lucide-bot size-5" />
      </button>
      <select
        v-if="hasAiAgents && showAgentList"
        class="px-2 py-1.5 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
        :value="selectedAgentId"
        @change="onAgentChange"
      >
        <option value="">{{ t('AI_AGENTS.CONVERSATION.AUTO_AGENT') }}</option>
        <option v-for="agent in aiAgents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <button
        v-tooltip="t('CONVERSATION.SEARCH_MESSAGES')"
        class="p-2 rounded-lg transition-colors hover:bg-n-slate-6"
        :class="showSearch ? 'text-n-brand' : 'text-n-slate-10'"
        @click="emit('toggle-search')"
      >
        <span class="i-lucide-search size-5" />
      </button>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
